import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchClients,
  fetchClient,
  createClient,
  updateClient,
  deleteClient,
  restoreClient,
} from '@/lib/api/clients'
import { broadcastInvalidation } from '@/lib/sync'
import type { ClientFormData, ClientFilters } from '@/types/clients'
import { toast } from 'sonner'

/**
 * Query key factory for clients
 * Provides consistent keys for cache management
 */
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: ClientFilters) =>
    [...clientKeys.lists(), organizationId, filters] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
}

/**
 * Fetch all clients for current organization with optional filtering
 */
export function useClients(filters?: ClientFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: clientKeys.list(organizationId || '', filters),
    queryFn: () => fetchClients(organizationId!, filters),
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a single client by ID
 */
export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: clientKeys.detail(id || ''),
    queryFn: () => fetchClient(id!),
    enabled: !!id,
  })
}

/**
 * Create a new client
 */
export function useCreateClient() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (data: ClientFormData) =>
      createClient({
        ...data,
        organization_id: currentOrganization!.id,
      }),
    onSuccess: () => {
      const keys = [clientKeys.lists(), ['client-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Client created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create client', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing client
 */
export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClientFormData> }) =>
      updateClient(id, data),
    onSuccess: (_, { id }) => {
      const keys = [clientKeys.lists(), clientKeys.detail(id), ['client-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Client updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update client', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a client (soft delete)
 */
export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteClient(id),
    onSuccess: () => {
      const keys = [clientKeys.lists(), ['client-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Client deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete client', {
        description: error.message,
      })
    },
  })
}

/**
 * Restore a soft-deleted client
 */
export function useRestoreClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => restoreClient(id),
    onSuccess: () => {
      const keys = [clientKeys.lists(), ['client-registry']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Client restored successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to restore client', {
        description: error.message,
      })
    },
  })
}
